// Modale obligatoire : choix du lieu de travail par défaut (multi-lieux / any_site)

import React, { useMemo, useState } from 'react';
import { Modal, Select, Typography, Alert, message } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useAppTranslation } from '../hooks/useAppTranslation';
import { canSellFromAnySite, getSalesSiteUiLabels } from '../types/business';
import { useCurrentEmployee, useSetMyDefaultSite } from '../hooks/useEmployees';
import { useAccessibleSalesSites } from '../hooks/useAccessibleSalesSites';
import { getErrorMessage } from '../utils/i18n/getErrorMessage';

const { Text, Paragraph } = Typography;

/**
 * Affichée quand le business est en mode « Tous les lieux » et que
 * l’employé connecté n’a pas encore de lieu de travail par défaut valide.
 * Non bloquante pour Super Admin / CSM sans fiche employé.
 */
const DefaultSiteRequiredModal: React.FC = () => {
  const { t } = useAppTranslation(['common']);
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;
  const hasEmployeeLink = !!(businessId && userProfile?.employeeIds?.[businessId]);
  const sellFromAnySite = canSellFromAnySite(currentBusiness);
  const siteLabels = getSalesSiteUiLabels(currentBusiness);
  const {
    accessibleSites,
    isLoading: sitesLoading,
    employee,
  } = useAccessibleSalesSites(businessId);
  const { isLoading: employeeLoading, isFetched } = useCurrentEmployee();
  const setDefaultSite = useSetMyDefaultSite();
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>();

  const favoriteStillValid = useMemo(() => {
    if (!employee?.defaultSiteId) return false;
    return accessibleSites.some((s) => s.id === employee.defaultSiteId);
  }, [employee?.defaultSiteId, accessibleSites]);

  const mustChoose =
    sellFromAnySite &&
    hasEmployeeLink &&
    !employeeLoading &&
    isFetched &&
    !sitesLoading &&
    accessibleSites.length > 0 &&
    !favoriteStillValid;

  const lockedSingle = accessibleSites.length === 1;

  const handleConfirm = async () => {
    const siteId = lockedSingle ? accessibleSites[0]?.id : selectedSiteId;
    if (!siteId) {
      message.warning(t('site.selectRequired', { site: siteLabels.siteNoun }));
      return;
    }
    try {
      await setDefaultSite.mutateAsync(siteId);
      message.success(t('site.workplaceSaved'));
    } catch (e: any) {
      message.error(getErrorMessage(e, t) || t('site.saveSiteError'));
    }
  };

  if (!mustChoose) return null;

  return (
    <Modal
      open
      closable={false}
      maskClosable={false}
      keyboard={false}
      title={
        <span>
          <EnvironmentOutlined style={{ marginRight: 8 }} />
          {t('site.defaultWorkplaceTitle')}
        </span>
      }
      okText={t('actions.confirm')}
      cancelButtonProps={{ style: { display: 'none' } }}
      onOk={handleConfirm}
      confirmLoading={setDefaultSite.isPending}
      destroyOnClose
    >
      <Paragraph>
        {t('site.defaultWorkplaceIntro', {
          businessName: currentBusiness?.name,
          siteNoun: siteLabels.siteNoun,
        })}
      </Paragraph>
      {employee?.defaultSiteId && !favoriteStillValid && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('site.previousSiteUnavailable')}
        />
      )}
      <Select
        style={{ width: '100%' }}
        size="large"
        placeholder={t('site.selectSitePlaceholder', { siteNoun: siteLabels.siteNoun })}
        value={lockedSingle ? accessibleSites[0]?.id : selectedSiteId}
        onChange={setSelectedSiteId}
        disabled={lockedSingle}
        options={accessibleSites.map((s) => ({ value: s.id, label: s.name }))}
        showSearch
        optionFilterProp="label"
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
        {t('site.changeLaterInAccount')}
        {lockedSingle ? t('site.singleSiteAssigned') : '.'}
      </Text>
    </Modal>
  );
};

export default DefaultSiteRequiredModal;
